import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Hls from 'hls.js';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { Cctv } from '../../types/cctv';
import { pickCctvLivePlayback, resolveAbsoluteCctvStreamUrl } from '../../utils/cctvStreamUrl';
import { usePlaybackConfig } from '../../hooks/usePlaybackConfig';
import { API_ORIGIN } from '../../config';
import { getBearerTokenForApi } from '../../utils/poolSiteAuth';
import {
  fetchStreamJpegBlob,
  isHttpUrl,
  isRtspUrl,
} from '../../utils/streamPreviewSnapshot';
import Go2rtcWhepPlayer from './Go2rtcWhepPlayer';

const SNAPSHOT_INTERVAL_MS = 2500;

interface CctvPreviewProps {
  cctv: Cctv;
  isInitiallyMuted?: boolean;
  onClose?: () => void;
  /** 시설 현장 게이트웨이 ON 여부. 미지정이면 pool_id로 조회 */
  siteGatewayEnabled?: boolean;
  /**
   * true: WebRTC/HLS 생략, stream_url JPEG 스냅샷만 (CCTV 등록·미저장).
   * 등록 시 id 없어 hls-proxy/WebRTC가 실패하는 경우용.
   */
  forceSnapshot?: boolean;
}

/** 등록 폼에 넣은 원본 URL (검증 대상) */
function pickInputStreamUrl(cctv: Cctv): string {
  const candidates = [
    cctv?.stream_url,
    (cctv as { url?: string }).url,
    cctv?.playback_url,
  ];
  for (const c of candidates) {
    const t = (c || '').trim();
    if (t) return t;
  }
  return '';
}

export default function CctvPreview({
  cctv,
  isInitiallyMuted = true,
  onClose = () => {},
  siteGatewayEnabled: siteGatewayEnabledProp,
  forceSnapshot = false,
}: CctvPreviewProps) {
  const { useRemotePlayback, loaded: playbackConfigLoaded, centralGo2rtcAvailable, centralGo2rtcStreams } =
    usePlaybackConfig();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const snapshotUrlRef = useRef<string | null>(null);
  const loadGenRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(isInitiallyMuted);
  const [snapshotSrc, setSnapshotSrc] = useState<string | null>(null);
  const [mode, setMode] = useState<'snapshot' | 'video'>('snapshot');
  const [siteGatewayEnabled, setSiteGatewayEnabled] = useState(
    Boolean(siteGatewayEnabledProp)
  );
  const [dataGatewayLoaded, setDataGatewayLoaded] = useState(
    siteGatewayEnabledProp !== undefined
  );

  useEffect(() => {
    if (siteGatewayEnabledProp !== undefined) {
      setSiteGatewayEnabled(Boolean(siteGatewayEnabledProp));
      setDataGatewayLoaded(true);
      return;
    }
    const pid = Number(cctv?.pool_id);
    if (!Number.isFinite(pid) || pid <= 0) {
      setSiteGatewayEnabled(false);
      setDataGatewayLoaded(true);
      return;
    }
    let cancelled = false;
    setDataGatewayLoaded(false);
    (async () => {
      try {
        const token = getBearerTokenForApi();
        const res = await axios.get(`${API_ORIGIN}/api/pools/${pid}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setSiteGatewayEnabled(Boolean(res.data?.site_gateway_enabled));
        }
      } catch {
        if (!cancelled) setSiteGatewayEnabled(false);
      } finally {
        if (!cancelled) setDataGatewayLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cctv?.pool_id, siteGatewayEnabledProp]);

  const inputUrl = useMemo(() => pickInputStreamUrl(cctv), [cctv]);

  const livePlayback = useMemo(() => {
    if (!playbackConfigLoaded || !dataGatewayLoaded) {
      return { mode: 'none' as const, webrtcUrl: '', hlsUrl: '' };
    }
    return pickCctvLivePlayback(
      {
        id: cctv?.id,
        pool_id: cctv?.pool_id,
        stream_url: cctv?.stream_url,
        url: (cctv as { url?: string }).url,
        playback_url: cctv?.playback_url,
        webrtc_url: cctv?.webrtc_url,
      },
      useRemotePlayback,
      siteGatewayEnabled,
      centralGo2rtcAvailable,
      centralGo2rtcStreams
    );
  }, [
    cctv?.id,
    cctv?.pool_id,
    cctv?.stream_url,
    cctv?.playback_url,
    cctv?.webrtc_url,
    (cctv as { url?: string }).url,
    useRemotePlayback,
    playbackConfigLoaded,
    dataGatewayLoaded,
    siteGatewayEnabled,
    centralGo2rtcAvailable,
    centralGo2rtcStreams,
  ]);

  const streamKey =
    cctv?.pool_id && cctv?.id ? `p${cctv.pool_id}_c${cctv.id}` : 'new';

  const [playbackKind, setPlaybackKind] = useState<'webrtc' | 'hls' | 'snapshot'>('snapshot');
  const webrtcFallbackRef = useRef(false);

  const webrtcActive =
    playbackKind === 'webrtc' &&
    Boolean(livePlayback.webrtcUrl) &&
    !webrtcFallbackRef.current;

  const showWebrtcLabel =
    playbackKind === 'webrtc' && Boolean(livePlayback.webrtcUrl);

  useEffect(() => {
    if (livePlayback.mode === 'hls' && playbackKind === 'webrtc') {
      setPlaybackKind('hls');
    } else if (
      livePlayback.mode === 'webrtc' &&
      playbackKind !== 'webrtc' &&
      !webrtcFallbackRef.current
    ) {
      setPlaybackKind('webrtc');
    }
  }, [livePlayback.mode, playbackKind]);

  const handleWebrtcFallback = useCallback(() => {
    webrtcFallbackRef.current = true;
    setPlaybackKind('hls');
  }, []);

  const handleWebrtcPlaying = useCallback(() => {
    setPlaybackKind('webrtc');
    setLoading(false);
    setMode('video');
    setError(null);
  }, []);

  const playUrl = useMemo(() => {
    if (!playbackConfigLoaded || !dataGatewayLoaded) return '';
    return (
      livePlayback.hlsUrl ||
      resolveAbsoluteCctvStreamUrl(
        {
          id: cctv?.id,
          pool_id: cctv?.pool_id,
          stream_url: cctv?.stream_url,
          url: (cctv as { url?: string }).url,
          playback_url: cctv?.playback_url,
        },
        useRemotePlayback,
        siteGatewayEnabled
      )
    );
  }, [
    livePlayback.hlsUrl,
    cctv,
    useRemotePlayback,
    playbackConfigLoaded,
    dataGatewayLoaded,
    siteGatewayEnabled,
  ]);

  const clearSnapshot = useCallback(() => {
    if (snapshotUrlRef.current) {
      URL.revokeObjectURL(snapshotUrlRef.current);
      snapshotUrlRef.current = null;
    }
    setSnapshotSrc(null);
  }, []);

  useEffect(() => {
    webrtcFallbackRef.current = false;
    setPlaybackKind(
      livePlayback.mode === 'webrtc' ? 'webrtc' : 'snapshot'
    );
    loadGenRef.current += 1;
    clearSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- CCTV 전환 시에만 재생 모드 리셋
  }, [streamKey, clearSnapshot]);

  const loadSnapshot = useCallback(
    async (url: string, gen: number) => {
      if (!url) return;
      try {
        const blob = await fetchStreamJpegBlob(url);
        if (gen !== loadGenRef.current) return;
        const obj = URL.createObjectURL(blob);
        if (snapshotUrlRef.current) URL.revokeObjectURL(snapshotUrlRef.current);
        snapshotUrlRef.current = obj;
        setSnapshotSrc(obj);
        setMode('snapshot');
        setPlaybackKind('snapshot');
        setError(null);
        setLoading(false);
      } catch (err) {
        if (gen !== loadGenRef.current) return;
        setLoading(false);
        setError(
          err instanceof Error && err.message
            ? `미리보기 실패: ${err.message}`
            : '미리보기 실패'
        );
      }
    },
    []
  );

  const initializeHlsStream = useCallback(
    (url: string, onFatal?: () => void) => {
      const video = videoRef.current;
      if (!video) {
        window.setTimeout(() => {
          if (videoRef.current) initializeHlsStream(url, onFatal);
        }, 50);
        return;
      }

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          liveSyncDurationCount: 3,
          backBufferLength: 30,
          maxBufferLength: 15,
          maxMaxBufferLength: 30,
          fragLoadingMaxRetry: 4,
          manifestLoadingMaxRetry: 4,
          manifestLoadingRetryDelay: 1000,
        });

        hlsRef.current = hls;
        hls.attachMedia(video);
        hls.loadSource(url);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLoading(false);
          setMode('video');
          setPlaybackKind('hls');
          setError(null);
          video.play().catch(() => {
            /* autoplay 제한 시 muted 재생 시도 */
            video.muted = true;
            video.play().catch(() => undefined);
          });
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
            return;
          }
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
          }
          hls.destroy();
          hlsRef.current = null;
          onFatal?.();
        });
        return;
      }

      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        video.addEventListener(
          'loadedmetadata',
          () => {
            setLoading(false);
            setMode('video');
            setPlaybackKind('hls');
            video.play().catch(() => undefined);
          },
          { once: true }
        );
        video.addEventListener(
          'error',
          () => {
            onFatal?.();
          },
          { once: true }
        );
        return;
      }

      onFatal?.();
    },
    []
  );

  useEffect(() => {
    // 등록 스냅샷 전용은 playback-config 대기 불필요 (지연·실패 시 미리보기 안 뜨는 것 방지)
    if (!forceSnapshot && (!playbackConfigLoaded || !dataGatewayLoaded)) {
      setLoading(true);
      return;
    }
    if (forceSnapshot && siteGatewayEnabledProp === undefined && !dataGatewayLoaded) {
      setLoading(true);
      return;
    }

    clearSnapshot();
    setError(null);

    const rawInput = (inputUrl || '').trim();

    // 등록(미저장): 스냅샷만 — HLS/WebRTC 경로 타지 않음
    const gen = ++loadGenRef.current;

    if (forceSnapshot) {
      if (!rawInput) {
        setLoading(false);
        setError('스트림 URL이 없음');
        return;
      }
      setMode('snapshot');
      setPlaybackKind('snapshot');
      setLoading(true);
      void loadSnapshot(rawInput, gen);
      const timer = window.setInterval(() => {
        void loadSnapshot(rawInput, gen);
      }, SNAPSHOT_INTERVAL_MS);
      return () => {
        window.clearInterval(timer);
        clearSnapshot();
      };
    }

    if (!playbackConfigLoaded || !dataGatewayLoaded) {
      setLoading(true);
      return;
    }

    // WebRTC 본선: RTSP 스냅샷·HLS 경로와 경쟁하지 않음 (구역 편집 RiskZoneEditor 와 동일)
    if (
      livePlayback.mode === 'webrtc' &&
      livePlayback.webrtcUrl &&
      !webrtcFallbackRef.current
    ) {
      setPlaybackKind('webrtc');
      clearSnapshot();
      setMode('video');
      setLoading(false);
      setError(null);
      return () => {
        loadGenRef.current += 1;
        clearSnapshot();
      };
    }

    // stream_url(RTSP) 우선. playUrl 절대경로 변환으로 RTSP가 깨진 경우 방지
    const source = (
      (isRtspUrl(inputUrl) ? inputUrl : '') ||
      inputUrl ||
      playUrl ||
      ''
    ).trim();
    if (!source) {
      setLoading(false);
      setError('스트림 URL이 없음');
      return;
    }

    setLoading(true);

    // RTSP: 브라우저 불가 → 서버/로컬 스냅샷
    if (isRtspUrl(source) || isRtspUrl(inputUrl)) {
      const rtspTarget = isRtspUrl(inputUrl) ? inputUrl.trim() : source;
      setMode('snapshot');
      void loadSnapshot(rtspTarget, gen);
      const timer = window.setInterval(() => {
        void loadSnapshot(rtspTarget, gen);
      }, SNAPSHOT_INTERVAL_MS);
      return () => {
        window.clearInterval(timer);
        clearSnapshot();
      };
    }

    // HTTP(S): 먼저 브라우저 재생, 실패 시 서버 스냅샷
    if (isHttpUrl(source) || isHttpUrl(playUrl)) {
      const videoTarget = isHttpUrl(playUrl) ? playUrl : source;
      const snapshotTarget = isRtspUrl(inputUrl) ? inputUrl.trim() : source;
      const lower = videoTarget.toLowerCase();

      const fallbackToSnapshot = () => {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        setMode('snapshot');
        void loadSnapshot(snapshotTarget, gen);
      };

      if (lower.includes('.m3u8')) {
        setMode('video');
        let settled = false;
        const fallbackToSnapshot = () => {
          if (settled) return;
          settled = true;
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
          setMode('snapshot');
          void loadSnapshot(snapshotTarget, gen);
        };
        const markVideoOk = () => {
          settled = true;
          window.clearTimeout(failSafe);
        };
        const failSafe = window.setTimeout(fallbackToSnapshot, 10000);
        const start = () => {
          if (!videoRef.current) {
            window.requestAnimationFrame(start);
            return;
          }
          const video = videoRef.current;
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
          if (Hls.isSupported()) {
            const hls = new Hls({
              enableWorker: true,
              lowLatencyMode: false,
              liveSyncDurationCount: 3,
              backBufferLength: 30,
              maxBufferLength: 15,
              maxMaxBufferLength: 30,
              fragLoadingMaxRetry: 4,
              manifestLoadingMaxRetry: 4,
              manifestLoadingRetryDelay: 1000,
            });
            hlsRef.current = hls;
            hls.attachMedia(video);
            hls.loadSource(videoTarget);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              markVideoOk();
              setLoading(false);
              setMode('video');
              setPlaybackKind('hls');
              setError(null);
              video.play().catch(() => {
                video.muted = true;
                video.play().catch(() => undefined);
              });
            });
            hls.on(Hls.Events.ERROR, (_event, data) => {
              if (!data.fatal) return;
              if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                hls.startLoad();
                return;
              }
              if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                hls.recoverMediaError();
                return;
              }
              hls.destroy();
              hlsRef.current = null;
              fallbackToSnapshot();
            });
          } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = videoTarget;
            video.addEventListener(
              'loadedmetadata',
              () => {
                markVideoOk();
                setLoading(false);
                setMode('video');
                video.play().catch(() => undefined);
              },
              { once: true }
            );
            video.addEventListener('error', () => fallbackToSnapshot(), { once: true });
          } else {
            fallbackToSnapshot();
          }
        };
        start();
        return () => {
          window.clearTimeout(failSafe);
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
          clearSnapshot();
        };
      }

      setMode('video');
      const video = videoRef.current;
      if (video) {
        video.src = videoTarget;
        video
          .play()
          .then(() => {
            setLoading(false);
            setMode('video');
          })
          .catch(() => fallbackToSnapshot());
        video.onerror = () => fallbackToSnapshot();
      } else {
        fallbackToSnapshot();
      }
      return () => {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        clearSnapshot();
      };
    }

    // 기타: 서버 스냅샷 시도
    setMode('snapshot');
    void loadSnapshot(source, gen);
    return () => clearSnapshot();
  }, [
    inputUrl,
    playUrl,
    playbackKind,
    livePlayback.mode,
    livePlayback.webrtcUrl,
    playbackConfigLoaded,
    dataGatewayLoaded,
    initializeHlsStream,
    loadSnapshot,
    clearSnapshot,
    forceSnapshot,
  ]);

  const handleRefresh = () => {
    if (webrtcActive) {
      loadGenRef.current += 1;
      setLoading(true);
      setError(null);
      webrtcFallbackRef.current = false;
      setPlaybackKind('webrtc');
      return;
    }
    const source = (inputUrl || playUrl || '').trim();
    if (!source) return;
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    if (forceSnapshot || mode === 'snapshot' || isRtspUrl(source)) {
      void loadSnapshot(isRtspUrl(inputUrl) ? inputUrl.trim() : source, gen);
      return;
    }
    if (playUrl.includes('.m3u8') || source.includes('.m3u8')) {
      initializeHlsStream(isHttpUrl(playUrl) ? playUrl : source, () =>
        loadSnapshot(source, gen)
      );
    }
  };

  const showVideo = mode === 'video';
  const rotate180 = Boolean(cctv?.rotate_180);

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 280,
        overflow: 'hidden',
        bgcolor: 'black',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 0,
          }}
        >
          {/* 영상만 180 — WebRTC 점·로딩·오류는 화면 좌표 고정 */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: rotate180 ? 'rotate(180deg)' : undefined,
            }}
          >
            {/* video는 모드 전환 시에도 마운트 유지(HLS attach용) */}
            {webrtcActive ? (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 1 }}>
                <Go2rtcWhepPlayer
                  key={livePlayback.webrtcUrl}
                  whepUrl={livePlayback.webrtcUrl}
                  muted={isMuted}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    backgroundColor: '#000',
                  }}
                  onFallback={handleWebrtcFallback}
                  onPlaying={handleWebrtcPlaying}
                />
              </Box>
            ) : (
              <video
                ref={videoRef}
                muted={isMuted}
                autoPlay
                playsInline
                controls={showVideo}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#000',
                  display: showVideo ? 'block' : 'none',
                }}
              />
            )}
            {!webrtcActive && !showVideo && snapshotSrc && (
              <img
                src={snapshotSrc}
                alt={`${cctv.name} 미리보기`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#000',
                }}
              />
            )}
          </Box>
          {showWebrtcLabel && (
            <Tooltip title="WebRTC" placement="right" enterDelay={200}>
              <Box
                aria-label="WebRTC"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#66bb6a',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
                  zIndex: 3,
                  cursor: 'default',
                }}
              />
            </Tooltip>
          )}

          {loading && !webrtcActive && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.45)',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && !webrtcActive && (
            <Alert
              severity="error"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                maxWidth: 420,
                zIndex: 2,
              }}
            >
              <AlertTitle>오류</AlertTitle>
              {error}
              <Button
                variant="contained"
                color="primary"
                onClick={handleRefresh}
                sx={{ mt: 1 }}
              >
                다시 시도
              </Button>
            </Alert>
          )}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 1,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle2" color="white" noWrap sx={{ maxWidth: '60%' }}>
            {cctv.name}
            {showWebrtcLabel
              ? ' · WebRTC'
              : playbackKind === 'snapshot' || mode === 'snapshot'
                ? ' · 스냅샷'
                : showVideo
                  ? ' · HLS'
                  : ''}
          </Typography>
          <Box>
            {(showVideo || showWebrtcLabel) && (
              <Tooltip title={isMuted ? '음소거 해제' : '음소거'}>
                <IconButton
                  size="small"
                  onClick={() => setIsMuted(!isMuted)}
                  sx={{ color: 'white' }}
                >
                  {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="새로고침">
              <IconButton size="small" onClick={handleRefresh} sx={{ color: 'white' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="닫기">
              <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
